import React, { useState, useCallback, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");

export default function LenderReport({ active }) {
  const defaultMonth = () => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  };
  const [month, setMonth] = useState(defaultMonth());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filings, setFilings] = useState([]);

  const loadFilings = useCallback(() => {
    axios.get(`${API}/lender-report-filings`).then((r) => setFilings(r.data)).catch(() => {});
  }, []);
  useEffect(() => { if (active === "rpt") loadFilings(); }, [active, loadFilings]);

  const load = useCallback(async (m) => {
    setLoading(true);
    setReport(null);
    try {
      const res = await axios.get(`${API}/lender-report/${m}`);
      setReport(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate report");
    }
    setLoading(false);
  }, []);

  const fileReport = async () => {
    try {
      const res = await axios.post(`${API}/lender-report/${month}/file`);
      toast.success(`Pipeline report ${month} filed to the record — ${fmt(res.data.filed_at)} (day-15 cadence item cleared by this filing)`);
      loadFilings();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not file the report");
    }
  };

  const filing = filings.find((f) => f.month === month);

  const exportPdf = () => {
    document.body.classList.add("report-export");
    setTimeout(() => {
      window.print();
      document.body.classList.remove("report-export");
    }, 200);
  };

  const kv = (obj) => Object.entries(obj || {});

  return (
    <section className={`chapter utility${active === "rpt" ? " active" : ""}`} id="rpt">
      <div className="eyebrow">Working record · executes ECH-SOP-14.1 — due day 15, never before MOCR closes</div>
      <h1>Monthly Pipeline Report (Lender)</h1>
      <p className="lede">Assembled automatically from the case pipeline, deadline calendar, intake-determination log, credential dashboard, and compliance records — and gated on the MOCR: if the month's MOCR is not closed, the report shows BLOCKED and does not issue.</p>

      <div className="month-bar">
        <label>Report month</label>
        <input type="month" className="inp month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="report-month" />
        <button className="btn ghost" onClick={() => load(month)} disabled={loading} data-testid="report-generate">Generate</button>
        {report && report.mocr_gate.status === "CLEAR" && (
          <button className="btn" onClick={exportPdf} data-testid="report-export-pdf">Export / Print report</button>
        )}
        {report && report.mocr_gate.status === "CLEAR" && (
          filing ? (
            <span className="vr-pill cleared" data-testid="report-filed-pill">FILED {fmt(filing.filed_at)}{filing.filed_by ? ` — ${filing.filed_by.name}` : ""}</span>
          ) : (
            <button className="btn" onClick={fileReport} data-testid="report-file-btn" title="Records the day-15 filing with a server-set timestamp — this is what clears the day-15 cadence deadline. No backdating: filing late shows the actual date.">
              File to the record (day-15 close)
            </button>
          )
        )}
      </div>

      {report && (
        <div className="report-body" data-testid="report-body">
          <div className={report.mocr_gate.status === "CLEAR" ? "pc-decision accept" : "pc-decision decline"} data-testid="report-gate">
            MOCR gate: <b>{report.mocr_gate.status}</b>{report.mocr_gate.status === "CLEAR" ? ` — MOCR ${report.month} closed ${fmt(report.mocr_gate.closed_at)}. Report may issue to the noteholder.` : ` — ${report.mocr_gate.reason}`}
          </div>

          <div className="formcard">
            <div className="formcard-title">Pipeline — {report.month}</div>
            <table className="mform-table"><tbody>
              <tr><th>Measure</th><th style={{ width: 120 }}>Value</th></tr>
              <tr><td>Cases on record (total)</td><td>{report.pipeline.cases_total}</td></tr>
              <tr><td>Cases staffed in month</td><td>{report.pipeline.cases_staffed_in_month}</td></tr>
              <tr><td>Claims tracked (total)</td><td>{report.pipeline.claims_tracked}</td></tr>
              <tr><td>New remittances (allowed amounts) in month</td><td>{report.pipeline.new_remittances_in_month}</td></tr>
              <tr><td>Cash collected (cases) in month</td><td>{report.pipeline.cash_collected_in_month}</td></tr>
              {kv(report.pipeline.claim_stage_counts).map(([k, v]) => (
                <tr key={k}><td>Claims — {k}</td><td>{v}</td></tr>
              ))}
              {kv(report.pipeline.case_status_counts).map(([k, v]) => (
                <tr key={k}><td>Cases — {k}</td><td>{v}</td></tr>
              ))}
            </tbody></table>
          </div>

          <div className="formcard">
            <div className="formcard-title">KPIs (SOP 14.2 standing metrics)</div>
            <table className="mform-table"><tbody>
              <tr><th>Metric</th><th style={{ width: 220 }}>Value / standard</th></tr>
              <tr><td>Intake determinations in month (accepted / ineligible)</td><td>{report.kpis.preclears_in_month} ({report.kpis.preclear_accepts} / {report.kpis.preclear_declines})</td></tr>
              <tr><td>Overdue statutory windows</td><td>{report.kpis.overdue_statutory_windows} — standard: zero (MOCR A-4)</td></tr>
              <tr><td>Missed-window rate (windows blown / windows concluded)</td><td data-testid="lender-missed-window-rate">{report.kpis.missed_window_rate_pct != null ? `${report.kpis.missed_window_rate_pct}%` : "—"} ({report.kpis.windows_missed} of {report.kpis.windows_evaluated}) — every miss converts modeled recovery to near-zero; feeds eligibility definitions</td></tr>
              <tr><td>Documentation packages overdue (24h rule)</td><td>{report.kpis.documentation_overdue_cases} — standard: zero</td></tr>
              <tr><td>Assists on dashboard</td><td>{report.kpis.assists_active}</td></tr>
              <tr><td>Assists lapsed (scheduling suspended)</td><td>{report.kpis.assists_lapsed} — standard: zero (MOCR A-7)</td></tr>
              <tr><td>Assists in 60-day expiration window</td><td>{report.kpis.assists_expiring_60d}</td></tr>
              <tr><td>Billed value tracked (all claims)</td><td>${Number(report.kpis.billed_tracked_total || 0).toLocaleString()}</td></tr>
              <tr><td>Determinations to date / win rate</td><td>{report.kpis.determinations_total} / {report.kpis.win_rate_pct != null ? `${report.kpis.win_rate_pct}%` : "—"} (baseline 86%)</td></tr>
              <tr><td>Awards in month (count / $)</td><td>{report.kpis.awards_in_month_count} / ${Number(report.kpis.awards_in_month_sum || 0).toLocaleString()}</td></tr>
              <tr><td>Average realization (award vs. billed)</td><td>{report.kpis.avg_realization_pct != null ? `${report.kpis.avg_realization_pct}%` : "—"}</td></tr>
            </tbody></table>
          </div>

          <div className="formcard">
            <div className="formcard-title">Compliance attestations</div>
            <table className="mform-table"><tbody>
              <tr><th>Item</th><th style={{ width: 220 }}>Status</th></tr>
              <tr><td>Exclusion screening for {report.month} (by the 5th)</td><td>{report.compliance.exclusion_screening_done ? `DONE — ${fmt(report.compliance.screening_attested_at)}` : "NOT ON FILE"}</td></tr>
              <tr><td>Open CAPs</td><td>{report.compliance.open_caps}</td></tr>
              <tr><td>Manual acknowledgments on file (v{report.manual_version})</td><td>{report.compliance.acknowledgments_on_file}</td></tr>
            </tbody></table>
          </div>
          <p style={{ color: "var(--mute)", fontSize: "12.5px" }}>Generated {fmt(report.generated_at)} · deliver with the controlled-account statement · file a copy to the record (SOP 14.1 step 4).</p>

          {/* Print sheet — shown only during export */}
          <div className="report-sheet">
            <div className="sheet-head">
              <div>
                <div className="sheet-brand">TRUMBALL, LLC dba EXTENDED COVERAGE HEALTHCARE</div>
                <div className="sheet-title">Monthly Pipeline Report — {report.month}</div>
                <div className="sheet-sub">ECH-SOP-14.1 · issued after MOCR close · CONFIDENTIAL — prepared for the noteholder</div>
              </div>
              <div className="sheet-status">
                <div className="sheet-status-line"><b>MOCR gate</b> {report.mocr_gate.status}</div>
                <div className="sheet-status-line"><b>Generated</b> {fmt(report.generated_at)}</div>
              </div>
            </div>
            <div className="sheet-section">
              <div className="sheet-sec-title">Pipeline</div>
              <table className="sheet-table"><tbody>
                <tr><th>Measure</th><th style={{ width: 100 }}>Value</th></tr>
                <tr><td>Cases on record / staffed in month</td><td>{report.pipeline.cases_total} / {report.pipeline.cases_staffed_in_month}</td></tr>
                <tr><td>Claims tracked / new remittances in month</td><td>{report.pipeline.claims_tracked} / {report.pipeline.new_remittances_in_month}</td></tr>
                <tr><td>Cash collected in month (cases)</td><td>{report.pipeline.cash_collected_in_month}</td></tr>
                {kv(report.pipeline.claim_stage_counts).map(([k, v]) => (<tr key={k}><td>Claims — {k}</td><td>{v}</td></tr>))}
              </tbody></table>
            </div>
            <div className="sheet-section">
              <div className="sheet-sec-title">KPIs &amp; compliance</div>
              <table className="sheet-table"><tbody>
                <tr><td>Intake determinations (accepted / ineligible)</td><td>{report.kpis.preclear_accepts} / {report.kpis.preclear_declines}</td></tr>
                <tr><td>Overdue statutory windows (standard: zero)</td><td>{report.kpis.overdue_statutory_windows}</td></tr>
                <tr><td>Missed-window rate (eligibility metric)</td><td>{report.kpis.missed_window_rate_pct != null ? `${report.kpis.missed_window_rate_pct}%` : "—"} ({report.kpis.windows_missed}/{report.kpis.windows_evaluated})</td></tr>
                <tr><td>Documentation overdue (24h rule)</td><td>{report.kpis.documentation_overdue_cases}</td></tr>
                <tr><td>Assists — active / lapsed / expiring ≤60d</td><td>{report.kpis.assists_active} / {report.kpis.assists_lapsed} / {report.kpis.assists_expiring_60d}</td></tr>
                <tr><td>Exclusion screening for month</td><td>{report.compliance.exclusion_screening_done ? "DONE" : "NOT ON FILE"}</td></tr>
                <tr><td>Open CAPs</td><td>{report.compliance.open_caps}</td></tr>
              </tbody></table>
            </div>
            <div className="sheet-foot">Generated from the ECH compliance system · MOCR-gated per ECH-SOP-14.1 · deliver with the controlled-account statement.</div>
          </div>
        </div>
      )}
    </section>
  );
}
